import { useCallback, useRef } from "react";
import {
  AnyVariables,
  OperationResult,
  UseMutationState,
  UseQueryState,
} from "urql";

import { useSnackbar } from "@/components/ui-v2/Snackbar";
import { AppError } from "@/utils/errors";

import { BasicErrorFragment, ErrorWithDataFragment } from "./queries.graphql";

type UrqlState<
  Data extends { [key in string]?: any },
  Variables extends AnyVariables = AnyVariables,
> =
  | UseQueryState<Data, Variables>
  | UseMutationState<Data, Variables>
  | OperationResult<Data, Variables>;

export interface GetResponseFromStateArgs<
  Data extends { [key in string]?: any },
  Variables extends AnyVariables = AnyVariables,
> {
  state: UrqlState<Data, Variables>;
  redirectOnError?: boolean;
  ignoredErrorCodes?: string[];
  successMessage?: string;
  errorMessage?: string;
  showSnackbarOnError?: boolean;
  getErrorMessage?: (err: BasicErrorFragment) => string | null;
  onError?: (err: ErrorWithDataFragment) => void;
  preventDuplicateErrorMessages?: boolean;
}

export type ResponseData<Data extends { [key in string]?: any }> = {
  [key in keyof Data]?: Exclude<Data[key], { __typename: "Error" }>;
};

type GetResponseFromStateResponse<Data extends { [key in string]?: any }> = {
  data: ResponseData<Data>;
  error?: ErrorWithDataFragment;
};

type getResponseFromStateFnType = <
  Data extends { [key in string]?: any },
  Variables extends AnyVariables = AnyVariables,
>(
  args: GetResponseFromStateArgs<Data, Variables>,
) => GetResponseFromStateResponse<Data>;

const defaultGetErrorMessage = ({ message }: BasicErrorFragment) => {
  return message;
};

export const useUrqlResponseHandler = () => {
  const { showSuccess, showError } = useSnackbar(); // TODO: The useUrqlResponseHandler should not call snackbar functions during render. Instead, it should return the messages to be shown and let the calling component handle them.
  const errorHandled = useRef<boolean>(false);

  const getResponseFromState: getResponseFromStateFnType = useCallback(
    <Data extends { [key in string]?: any }, Variables extends AnyVariables>({
      state,
      successMessage,
      errorMessage,
      showSnackbarOnError = true,
      redirectOnError = false,
      ignoredErrorCodes = ["NOT_FOUND", "INVALID_JWT"],
      getErrorMessage = defaultGetErrorMessage,
      onError,
      preventDuplicateErrorMessages,
    }: GetResponseFromStateArgs<
      Data,
      Variables
    >): GetResponseFromStateResponse<Data> => {
      const response: GetResponseFromStateResponse<Data> = {
        data: {},
      };

      if (state.error) {
        response.error = {
          __typename: "Error",
          errorType: "INTERNAL_SERVER_ERROR",
          code: "NETWORK_ERROR",
          message: state.error.message,
          data: null,
        };

        if (!errorHandled.current && showSnackbarOnError) {
          showError(
            errorMessage ??
              "Something went wrong connecting to the server. Please try again.",
          );
        }

        if (onError) {
          onError(response.error);
        }

        if (preventDuplicateErrorMessages) {
          errorHandled.current = true;
        }

        return response;
      }

      if (!state.data) {
        return response;
      }

      const stateData = state.data;
      Object.keys(stateData).forEach((key: keyof Data) => {
        const dataValue = stateData[key];
        if (dataValue?.__typename === "Error") {
          response.error = {
            __typename: "Error",
            errorType: dataValue.errorType,
            code: dataValue.code,
            message: dataValue.message,
            data: dataValue.data,
          };
          if (ignoredErrorCodes.includes(response.error.code)) {
            // ignore
          } else {
            if (redirectOnError) {
              throw new AppError(dataValue.message, {
                code: dataValue.code,
              });
            } else {
              if (!errorHandled.current) {
                if (errorMessage) {
                  showError(errorMessage);
                } else if (showSnackbarOnError) {
                  const message = getErrorMessage(response.error);
                  if (message) {
                    showError(message);
                  }
                }
                if (onError) {
                  onError(response.error);
                }
              }
              if (preventDuplicateErrorMessages) {
                errorHandled.current = true;
              }
            }
          }
        } else {
          response.data[key] = dataValue;
          if (successMessage) {
            showSuccess(successMessage);
          }
        }
      });
      return response;
    },
    [showSuccess, showError],
  );

  const resetErrorHandled = useCallback(() => {
    errorHandled.current = false;
  }, []);

  return { getResponseFromState, resetErrorHandled };
};
